import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { customerService } from "../services/customerServices";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function ManageCustomer() {
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);
  const [allRegionsData, setAllRegionsData] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Add statistics state variables
  const [totalUsers, setTotalUsers] = useState(0);
  const [filteredUsersCount, setFilteredUsersCount] = useState(0);
  const [currentPageUsersCount, setCurrentPageUsersCount] = useState(0);

  // Fetch total count on component mount and when status/search filters change
  useEffect(() => {
    fetchTotalCount();
  }, []); // Only on mount - total count should be constant regardless of filters

  // Also fetch total count when status or search changes if you want dynamic total count
  // useEffect(() => {
  //   fetchTotalCount();
  // }, [status, search]); // Uncomment if you want total count to reflect filters

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, country, region, status]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, search, status]);

  // Calculate statistics whenever customers or filters change
  useEffect(() => {
    if (customers.length > 0) {
      const formattedCustomers = formatCustomers(customers);
      
      // Filter customers based on all criteria
      const filtered = formattedCustomers.filter((customer) => {
        const searchTerm = search.toLowerCase();
        
        const searchMatch =
          customer.id.toString().includes(searchTerm) ||
          customer.fullName.toLowerCase().includes(searchTerm) ||
          customer.email.toLowerCase().includes(searchTerm) ||
          customer.firstName.toLowerCase().includes(searchTerm) ||
          customer.surname.toLowerCase().includes(searchTerm);

        const countryMatch = country === "all" || 
          (customer.address.country && customer.address.country.toLowerCase() === country.toLowerCase());
        
        const regionMatch = region === "all" || 
          (customer.address.county && customer.address.county.toLowerCase() === region.toLowerCase());
        
        const statusMatch = status === "all" || 
          customer.status.toLowerCase() === status.toLowerCase();

        return searchMatch && statusMatch && countryMatch && regionMatch;
      });

      // Calculate paginated customers
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

      // Update statistics
      setFilteredUsersCount(filtered.length);
      setCurrentPageUsersCount(paginated.length);
    } else {
      setFilteredUsersCount(0);
      setCurrentPageUsersCount(0);
    }
  }, [customers, search, country, region, status, currentPage]);

  // Update available regions when country filter changes
  useEffect(() => {
    if (country === "all") {
      // Show all unique regions when no country is selected
      const uniqueRegions = [...new Set(allRegionsData.map(item => item.region))].sort();
      setAvailableRegions(uniqueRegions);
    } else {
      // Filter regions for the selected country
      const countryRegions = allRegionsData
        .filter(item => item.country.toLowerCase() === country.toLowerCase())
        .map(item => item.region);
      const uniqueRegions = [...new Set(countryRegions)].sort();
      setAvailableRegions(uniqueRegions);
    }
    
    // Reset region filter when country changes
    setRegion("all");
  }, [country, allRegionsData]);

  // Function to fetch total customer count
  const fetchTotalCount = async () => {
    try {
      // Pass filters if you want count based on current filters
      // If you want total regardless of filters, pass empty object
      const countResponse = await customerService.getCustomerCount({});
      setTotalUsers(countResponse.customer_count || countResponse.count || 0);
    } catch (error) {
      console.error('Error fetching total customer count:', error);
      setTotalUsers(0);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(status !== 'all' && { status }),
        ...(search && { search })
      };

      const response = await customerService.getAllCustomers(params);
      
      if (response.users && Array.isArray(response.users)) {
        setCustomers(response.users);
        
        // Extract unique countries from the response
        const countries = [...new Set(response.users
          .map(customer => customer.country)
          .filter(country => country && country.trim() !== '')
        )].sort();
        
        setAvailableCountries(countries);

        // Store all country-region pairs for filtering
        const regionsData = response.users
          .map(customer => ({
            country: customer.country,
            region: customer.region
          }))
          .filter(item => item.region && item.region.trim() !== '' && item.country && item.country.trim() !== '');
        
        setAllRegionsData(regionsData);

        // Set initial available regions (all unique regions)
        const uniqueRegions = [...new Set(regionsData.map(item => item.region))].sort();
        setAvailableRegions(uniqueRegions);
      } else {
        setCustomers([]);
        setAvailableCountries([]);
        setAvailableRegions([]);
        setAllRegionsData([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setSnackMessage('Failed to load customers');
      setSnackOpen(true);
      setCustomers([]);
      setAvailableCountries([]);
      setAvailableRegions([]);
      setAllRegionsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackClose = () => setSnackOpen(false);

  const goToCreateAccount = () => {
    navigate("/create-customer-account");
  };

  // Format API data to match your existing structure
  const formatCustomers = (apiCustomers) => {
    return apiCustomers.map(customer => ({
      id: customer.Customer_id,
      fullName: `${customer.first_name} ${customer.last_name}`,
      firstName: customer.first_name,
      surname: customer.last_name,
      email: customer.email,
      mobile: customer.phone_number,
      address: {
        line1: customer.address_line1 || '',
        line2: customer.address_line2 || '',
        line3: customer.address_line3 || '',
        city: customer.city || '',
        county: customer.region || 'N/A',
        country: customer.country || 'N/A',
        postcode: customer.postcode || ''
      },
      status: customer.current_status ? customer.current_status.charAt(0).toUpperCase() + customer.current_status.slice(1) : 'Awaiting',
      notes: customer.status_note || '',
      value: `${customer.wallet_details?.total_points || 0} TopCoin`,
      wallet_details: customer.wallet_details,
      transaction_summary: customer.transaction_summary,
      recent_transactions: customer.recent_transactions || [],
      referrer_name: customer.referer_name || 'N/A',
      referrer_id: customer.referer_id || 'N/A',
      referrer_agent_type: customer.referer_agent_type || 'N/A',
      referrer_country: customer.referer_country || 'N/A',
      referrer_region: customer.referer_region || 'N/A',
      referrer_email: customer.referer_email || 'N/A',
      referrer_phone: customer.referer_phone_number || 'N/A',
    }));
  };

  const handleRowClick = (customer) => {
    navigate("/customer-details", { state: { customer } });
  };

  // Use formatted customers from API, fallback to empty array
  const formattedCustomers = customers.length > 0 ? formatCustomers(customers) : [];
  
  // Enhanced search logic for ID, name, and email
  const filteredCustomers = formattedCustomers.filter((customer) => {
    const searchTerm = search.toLowerCase();
    
    const searchMatch =
      customer.id.toString().includes(searchTerm) ||
      customer.fullName.toLowerCase().includes(searchTerm) ||
      customer.email.toLowerCase().includes(searchTerm) ||
      customer.firstName.toLowerCase().includes(searchTerm) ||
      customer.surname.toLowerCase().includes(searchTerm);

    const countryMatch = country === "all" || 
      (customer.address.country && customer.address.country.toLowerCase() === country.toLowerCase());
    
    const regionMatch = region === "all" || 
      (customer.address.county && customer.address.county.toLowerCase() === region.toLowerCase());
    
    const statusMatch = status === "all" || 
      customer.status.toLowerCase() === status.toLowerCase();

    return searchMatch && statusMatch && countryMatch && regionMatch;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Manage Customers</h2>
            <button className="action-btn" onClick={goToCreateAccount}>+ Add New Customer</button>
          </div>

          {/* Statistics Section */}
          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total Customers:</span>
              <span className="stat-value">{totalUsers}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Customers:</span>
              <span className="stat-value">{filteredUsersCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">
                {paginatedCustomers.length}
              </span>
            </div>
          </div>

          <div className="filters">
            {/* ... your existing filter JSX ... */}
            <div className="input-group">
              <label>Filter by country:</label>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="filter-select">
                <option value="all">All Countries</option>
                {availableCountries.map((countryName, index) => (
                  <option key={index} value={countryName.toLowerCase()}>
                    {countryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by region:</label>
              <select 
                value={region} 
                onChange={(e) => setRegion(e.target.value)} 
                className="filter-select"
                disabled={availableRegions.length === 0}
              >
                <option value="all">
                  {availableRegions.length === 0 ? "No regions available" : "All Regions"}
                </option>
                {availableRegions.map((regionName, index) => (
                  <option key={index} value={regionName.toLowerCase()}>
                    {regionName}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Filter by status:</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="filter-select">
                <option value="all">All</option>
                <option value="approved">Approved</option>
                <option value="awaiting">Awaiting</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="input-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        {/* ... your existing table JSX ... */}
        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "40px" }}>ID</th>
                <th style={{ width: "250px" }}>Full Name</th>
                <th style={{ width: "250px" }}>Email</th>
                <th style={{ width: "250px" }}>Region</th>
                <th style={{ width: "250px" }}>Country</th>
                <th style={{ width: "120px" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center" }}>
                    <div className="loading-message">Loading Customers...</div>
                  </td>
                </tr>
              ) : paginatedCustomers.length > 0 ? (
                paginatedCustomers.map((customer, i) => (
                  <tr 
                    key={customer.id || i}
                    onClick={() => handleRowClick(customer)}
                  >
                    <td>{customer.id}</td>
                    <td>{customer.fullName}</td>
                    <td>{customer.email}</td>
                    <td>{customer.address.county}</td>
                    <td>{customer.address.country}</td>
                    <td>
                      <span className={`status ${customer.status.toLowerCase()}`}>{customer.status}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: "center" }}>
                    {filteredUsersCount === 0 && totalUsers > 0 
                      ? "No customers match your filters" 
                      : "No customers found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!loading && filteredUsersCount > 0 && (
            <div className="pagination">
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className="page-btn" 
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      <Snackbar 
        open={snackOpen} 
        autoHideDuration={3000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          severity="success"
          icon={<span style={{ fontWeight: "bold", fontSize: "18px", color:'rgba(51, 178, 51, 1' }}>✔</span>}
          sx={{
              backgroundColor: "transparent",
              backdropFilter: "blur(8px)",
              color: "#000",
              border: "2px solid rgba(51, 178, 51, 1)",
              borderRadius: "10px",
              boxShadow: "none",
              fontSize: "15px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: '25px',
              width: '200px',
            }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageCustomer;